import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowRight,
  FileText,
  Rocket,
  Bike,
  GaugeCircle,
  Map,
  Star,
  Zap,
  Shield,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaFileInvoice, // For Return to Invoice
  FaHardHat, // For Helmet & Gear Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaUserShield,
  FaTire, // For Tyre Protection
  FaKey,
  FaTired, // For Key Loss Cover
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TriumphBikeInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is Triumph bike insurance mandatory in India?",
      answer:
        "Yes, third-party insurance is legally mandatory, but comprehensive insurance is highly recommended.",
    },
    {
      question: "What’s the best policy for Triumph Speed 400?",
      answer:
        "A comprehensive plan with zero depreciation and RSA for a balance of value and protection.",
    },
    {
      question: "Is the engine covered by default?",
      answer: "No, you'll need to opt for the Engine Protection add-on.",
    },
    {
      question: "Do I need to declare accessories or gear?",
      answer: "Yes, if you want them covered under the policy.",
    },
    {
      question: "What if I modify my Triumph?",
      answer:
        "Declare all modifications to your insurer to avoid rejection during claims.",
    },
    {
      question: "How much is insurance for the Tiger 900?",
      answer:
        "Premiums start at ₹10,000 and vary depending on your location and policy add-ons.",
    },
    {
      question: "Can I buy Triumph insurance online?",
      answer:
        "Yes, platforms like TheFinMaster let you buy, renew, and compare online.",
    },
    {
      question: "Will off-road use void my insurance?",
      answer:
        "Standard policies don’t cover off-roading unless specifically endorsed.",
    },
    {
      question: "Can I transfer my No Claim Bonus to a new Triumph?",
      answer:
        "Yes, NCB is transferrable between bikes and brands as long as you stay the policyholder.",
    },
    {
      question: "Does Triumph insurance cover flood or cyclone damage?",
      answer: "Yes, natural calamities are covered under comprehensive plans.",
    },
    {
      question: "How can I save on Triumph insurance premiums?",
      answer:
        "Opt for voluntary deductibles, accumulate NCB, and install certified anti-theft devices.",
    },
    {
      question: "What if I miss my policy renewal date?",
      answer:
        "You can still renew but may need bike inspection and lose your NCB.",
    },
    {
      question: "Does Triumph insurance cover track usage?",
      answer: "No, most standard plans exclude racing or track events.",
    },
    {
      question: "Is theft covered in all Triumph insurance policies?",
      answer: "Only comprehensive policies include theft coverage.",
    },
    {
      question: "Are Triumph service centers cashless under insurance?",
      answer:
        "Yes, most leading insurers have tie-ups with Triumph service centers.",
    },
    {
      question: "What documents are needed during claims?",
      answer:
        "RC, insurance policy, FIR (if needed), repair bills, claim form.",
    },
    {
      question: "Do I get full IDV in case of total loss?",
      answer:
        "You do if you have Return to Invoice cover; otherwise, claim is based on IDV minus depreciation.",
    },
    {
      question: "Can I renew my policy without inspection?",
      answer: "Yes, if renewed before expiry or within grace period.",
    },
    {
      question: "Are Triumph parts expensive to replace?",
      answer:
        "Yes, hence insurance helps avoid out-of-pocket costs for repairs.",
    },
    {
      question: "Can I insure imported Triumph models like Rocket 3?",
      answer: "Yes, with insurers that support imported or CBU bikes.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Full claim value without depreciation cuts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description: "Especially useful during monsoons or off-roading.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance (RSA)",
      description:
        "Crucial for long rides, includes towing and emergency help.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Get the full invoice value in case of theft or total loss.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers nuts, bolts, brake oil, coolant, and more.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Helmet & Gear Cover",
      description: "For Triumph riders who invest in high-end riding gear.",
      icon: <FaHardHat className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Tyre Protection",
      description: "For unexpected tyre or tube damage during touring.",
      icon: <FaTired className="text-4xl text-gray-600 mb-4" />,
    },
    {
      title: "Key Loss Cover",
      description: "Covers electronic key replacement.",
      icon: <FaKey className="text-4xl text-teal-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];

  const benefits = [
    {
      title: "Speed 400",
      desc: "A stylish, performance-packed roadster at an entry-level price.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Scrambler 400 X",
      desc: "A rugged all-terrain bike ideal for both city and off-road riding.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Trident 660",
      desc: "A mid-weight roadster with sharp handling and smooth power delivery.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Tiger 660 GT / Tiger 900 / Tiger 1200",
      desc: "Adventure-touring legends.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "Bonneville Series (T100, T120, Bobber)",
      desc: "Retro-styled classics with modern features.",
      icon: <Star className="w-7 h-7 text-yellow-500" />,
      gradient: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Street Triple / Speed Triple",
      desc: "Aggressive streetfighters built for pure adrenaline.",
      icon: <Zap className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "Rocket 3 R / GT",
      desc: "The world’s most powerful production motorcycle with massive road presence.",
      icon: <Shield className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100",
    },
  ];
  const triumphinsurance = [
    {
      name: "Speed T4",
      path: "/triumph-speed-t4-bike-insurance",
      image: "/assets/triumphspeedt4.jpeg",
    },
    {
      name: "Speed 400",
      path: "/triumph-speed-400-bike-insurance",
      image: "/assets/triumphspeed400.jpeg",
    },
     {
      name: "Scrambler 400X",
      path: "/triumph-scrambler-400-x-bike-insurance",
      image: "/assets/triumphscrambler400x.jpeg",
    },
     {
      name: "Thruxton 400",
      path: "/triumph-thruxton-400-bike-insurance",
      image: "/assets/triumphthruxton400.jpeg",
    },
      {
      name: "Trident 660",
      path: "/triumph-trident-660-bike-insurance",
      image: "/assets/triumphtrident660.jpeg",
    },
       {
      name: "Street Triple765",
      path: "/triumph-street-triple-765-bike-insurance",
      image: "/assets/triumphstreettriple765.jpeg",
    },
      {
      name: "Tiger 900",
      path: "/triumph-tiger-900-bike-insurance",
      image: "/assets/triumphtiger900.jpeg",
    },
    {
      name: "Rocket 3",
      path: "/triumph-rocket-3-bike-insurance",
      image: "/assets/triumphrocket3.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Triumph Bike Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Triumph bike insurance options in India. Compare quotes, customize coverage, and buy online to protect your Triumph Speed 400, Trident 660, or Rocket 3."
        />
        <meta
          name="keywords"
          content="triumph bike insurance, triumph speed 400 insurance, triumph tiger 900 insurance, two-wheeler insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/triumph-two-wheeler-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/triumph-bike-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Triumph Bike Insurance in India" />
        <meta property="og:description" content="Compare and buy Triumph bike insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/triumph-bike-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Triumph Bike Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Triumph Bike Insurance </span>{" "}
                  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Triumph is celebrated for its premium craftsmanship, strong
                  performance, and timeless British design. Whether you ride a
                  classic Bonneville or an adventure-ready Tiger, safeguarding
                  your bike starts with the right insurance coverage. This guide
                  takes you through everything you need to know about insuring a
                  Triumph bike, from average premiums and model insights to
                  useful add-ons, renewal tips, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/triumpinsurance.jpeg"
                alt="Triumph Bike"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About Triumph Motorcycles in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Triumph has earned a dedicated following in India for its
            performance, design, and heritage. With an expanding portfolio under
            the 400cc and premium segments, Triumph caters to both first-time
            premium bike owners and seasoned superbike enthusiasts.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="p-4 sm:p-8 bg-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
          Choose Your Bike Brand
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {triumphinsurance.map((calc, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={calc.path}
                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
              >
                <img
                  src={calc.image}
                  alt={calc.name}
                  className="w-16 h-16 object-contain mb-2"
                />
                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                  {calc.name}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why You Should Insure Your Triumph Bike
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Triumph motorcycles are high-performance machines with specialized
            parts and premium engineering. Having the right insurance plan helps
            with:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>
                Protection against accidents, fire, natural disasters, and theft
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Third-party legal coverage as per Indian motor laws</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>
                Reimbursement for costly repairs or parts replacements
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Support during long rides or adventure trips</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Coverage for imported or CBU models</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your Triumph stays protected, legally
            compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your TwoWheeler Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/bike-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Triumph Models & Insurance Premium Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Note: Premiums may vary based on location, usage, and policy type.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Speed 400",
                category: "Entry Roadster",
                idv: "₹2.30 – ₹2.70 Lakh",
                premium: "₹2,500 – ₹3,000",
              },
              {
                model: "Scrambler 400 X",
                category: "Dual-Purpose",
                idv: "₹2.80 – ₹3.10 Lakh",
                premium: "₹3,200 – ₹3,800",
              },
              {
                model: "Trident 660",
                category: "Mid Roadster",
                idv: "₹8.00 – ₹8.50 Lakh",
                premium: "₹6,000 – ₹8,000",
              },
              {
                model: "Tiger Sport 660",
                category: "Mid Adventure Tourer",
                idv: "₹9.00 – ₹10.00 Lakh",
                premium: "₹7,000 – ₹9,000",
              },
              {
                model: "Street Triple RS",
                category: "Streetfighter",
                idv: "₹11.00 – ₹12.50 Lakh",
                premium: "₹8,000 – ₹10,500",
              },
              {
                model: "Bonneville T120",
                category: "Classic",
                idv: "₹11.50 – ₹12.50 Lakh",
                premium: "₹8,000 – ₹10,000",
              },
              {
                model: "Tiger 900 Rally Pro",
                category: "ADV Touring",
                idv: "₹15.00 – ₹16.00 Lakh",
                premium: "₹10,000 – ₹13,000",
              },
              {
                model: "Rocket 3 R / GT",
                category: "Hyper Cruiser",
                idv: "₹21.00 – ₹22.00 Lakh",
                premium: "₹15,000 – ₹18,000",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Segment
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Speed 400",
                    "Entry Roadster",
                    "₹2.30 – ₹2.70 Lakh",
                    "₹2,500 – ₹3,000",
                  ],
                  [
                    "Scrambler 400 X",
                    "Dual-Purpose",
                    "₹2.80 – ₹3.10 Lakh",
                    "₹3,200 – ₹3,800",
                  ],
                  [
                    "Trident 660",
                    "Mid Roadster",
                    "₹8.00 – ₹8.50 Lakh",
                    "₹6,000 – ₹8,000",
                  ],
                  [
                    "Tiger Sport 660",
                    "Mid Adventure Tourer",
                    "₹9.00 – ₹10.00 Lakh",
                    "₹7,000 – ₹9,000",
                  ],
                  [
                    "Street Triple RS",
                    "Streetfighter",
                    "₹11.00 – ₹12.50 Lakh",
                    "₹8,000 – ₹10,500",
                  ],
                  [
                    "Bonneville T120",
                    "Classic",
                    "₹11.50 – ₹12.50 Lakh",
                    "₹8,000 – ₹10,000",
                  ],
                  [
                    "Tiger 900 Rally Pro",
                    "ADV Touring",
                    "₹15.00 – ₹16.00 Lakh",
                    "₹10,000 – ₹13,000",
                  ],
                  [
                    "Rocket 3 R / GT",
                    "Hyper Cruiser",
                    "₹21.00 – ₹22.00 Lakh",
                    "₹15,000 – ₹18,000",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Add-Ons for Triumph Bike Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Premium bikes deserve enhanced protection. Here are the add-ons worth
          considering for Triumph owners:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for Triumph Motorcycles in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Pick insurers with experience in covering premium and imported
          motorcycles:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Trusted insurer with Triumph-compatible garage network.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for wide garage network.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Reliable coverage with quick claim settlements.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              TATA AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers smart policy options for Triumph bikes.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">
              Seamless app experience and cashless claims.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Go Digit
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Hassle-free digital claims process.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🛡️</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              New India Assurance
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Reliable coverage with digital claims support.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Needed to Insure Your Triumph Bike
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your insurance, you’ll need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Owner's ID proof</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PUC certificate</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-size photograph</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance copy (for renewals)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Invoice copy (for new bikes)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About Triumph Bike Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Whether you're riding a Triumph Speed 400 through city lanes or
                cruising on a Rocket 3 across highways, your motorcycle is a
                symbol of power, elegance, and precision. Protecting it with a
                comprehensive insurance plan ensures peace of mind, financial
                protection, and the freedom to ride without worry.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Use TheFinMaster to compare Triumph bike insurance quotes,
                add-ons, and features all in one place. Experience a fast,
                hassle-free way to insure your dream machine.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default TriumphBikeInsurance;
